import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import type { YAxisPlotLinesOptions } from 'highcharts';

import alertChartsIcon from '@assets/svg/chart-icons/alertCharts-icon.svg?inline';

import { apiClient } from '@services/apiClient';

import type { IAlertCondition } from '../model/alertCondition';

interface IOptions {
  pointId: number;
  readingTypeId: number;
  alertTooltipId: string;
  unit?: string;
}

interface IResponse {
  data: Array<IAlertCondition>;
}

export const chartAlertConditionsKey = (pointId: number, readingTypeId: number) => [
  'chartAlertConditions',
  pointId,
  readingTypeId,
];
export const chartAlertConditionsFn = ({ queryKey }: QueryFunctionContext<(string | number)[]>) =>
  apiClient.get(`${APP_API_URL_V2}fsd/alert-conditions/chart-data`, {
    params: { installationPointId: queryKey[1], readingTypeId: queryKey[2] },
  });

const chartAlertConditionsSelect = (data: IResponse, alertTooltipId: string, unit?: string) => {
  const alertPlotLines: Array<YAxisPlotLinesOptions> = [];
  let maxAlertValue: number | null = null;

  if (data.data?.length) {
    data.data.forEach((condition) => {
      const alertColor = condition.alertLevelName === 'Warning' ? '#F24F52' : '#F8C71C';
      const alertInfo = JSON.stringify({
        ...condition,
        chartUnit: unit,
      });

      if (!maxAlertValue || maxAlertValue < condition.value) {
        maxAlertValue = condition.value;
      }

      alertPlotLines.push({
        color: alertColor,
        width: 2,
        value: condition.value,
        dashStyle: 'Dash',
        label: {
          align: 'right',
          useHTML: true,
          y: 3,
          text: `<div class="alert-plot-line" data-tooltip-id="${alertTooltipId}" data-tooltip-content='${alertInfo}'>
            <div><span style="background-color: ${alertColor};"></span><img src="${alertChartsIcon}" alt="alert-icon" /></div></div>`,
        },
        zIndex: 3,
      });
    });
  }

  return { alertPlotLines, maxAlertValue };
};

export const useChartAlertConditionsQuery = ({ pointId, readingTypeId, alertTooltipId, unit }: IOptions) => {
  return useQuery({
    // @ts-ignore TODO: update react-query to v5
    queryKey: chartAlertConditionsKey(pointId, readingTypeId),
    queryFn: chartAlertConditionsFn,
    enabled: !!pointId,
    placeholderData: { data: { data: { alertPlotLines: [], maxAlertValue: null } } },
    select: ({ data }: AxiosResponse<IResponse>) => chartAlertConditionsSelect(data, alertTooltipId, unit),
  });
};
